import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormClearErrors,
  type UseFormRegister,
  type UseFormSetError,
} from 'react-hook-form';
import {
  FieldError,
  FieldLabel,
  SectionCard,
  campInputClassFor,
} from '@/features/campaigns/components/FormPrimitives';
import { inputValidation } from '@/utils/function';
import {
  formatMobileDisplay,
  parseMobileInput,
  type MobileConfig,
} from '@/features/auth/utils/mobileNumber';
import {
  isValidCreateClientEmail,
  type AddClientFormValues,
} from '../AddClientPage.types';

type ContactPersonSectionProps = {
  register: UseFormRegister<AddClientFormValues>;
  control: Control<AddClientFormValues>;
  errors: FieldErrors<AddClientFormValues>;
  mobileConfig: MobileConfig;
  setError: UseFormSetError<AddClientFormValues>;
  clearErrors: UseFormClearErrors<AddClientFormValues>;
  emailReadOnly?: boolean;
  variant?: 'card' | 'plain';
};

export function ContactPersonSection({
  register,
  control,
  errors,
  mobileConfig,
  setError,
  clearErrors,
  emailReadOnly = false,
  variant = 'card',
}: ContactPersonSectionProps) {
  const inputClass = campInputClassFor(variant);
  const isPlain = variant === 'plain';

  return (
    <SectionCard
      step={2}
      title="Contact person"
      description="Primary person we can reach"
      variant={variant}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <FieldLabel required>Key Contact Person Name</FieldLabel>
          <input
            type="text"
            placeholder="Full name"
            className={inputClass}
            aria-invalid={Boolean(errors.contactPersonName)}
            {...register('contactPersonName', {
              required: 'Contact person is required',
              setValueAs: (value) => inputValidation(String(value ?? '')),
              onChange: (e) => {
                e.target.value = inputValidation(e.target.value);
              },
            })}
          />
          <FieldError message={errors.contactPersonName?.message} />
        </div>
        <div>
          <FieldLabel required>Contact Person Mobile</FieldLabel>
          <div
            className={`flex h-11 overflow-hidden rounded-xl ${
              isPlain
                ? errors.mobileNumber
                  ? 'bg-[#FFF1F1] ring-2 ring-red-300'
                  : 'bg-[#EEF2F6]'
                : errors.mobileNumber
                  ? 'border border-red-400 bg-[var(--dash-soft)]'
                  : 'border border-[var(--dash-line)] bg-[var(--dash-soft)]'
            }`}
          >
            <span
              className={`flex h-full shrink-0 items-center gap-1.5 px-3 text-[12px] font-semibold text-[var(--dash-ink)] ${
                isPlain
                  ? 'bg-[#E4EAF0]'
                  : 'border-r border-[var(--dash-line)] bg-white/70'
              }`}
              title={mobileConfig.label}
            >
              <span aria-hidden>{mobileConfig.flag}</span>
              <span>{mobileConfig.code}</span>
            </span>
            <Controller
              name="mobileNumber"
              control={control}
              rules={{
                required: 'Mobile is required',
                minLength: {
                  value: mobileConfig.length,
                  message: mobileConfig.message,
                },
                maxLength: {
                  value: mobileConfig.length,
                  message: mobileConfig.message,
                },
                pattern: {
                  value: mobileConfig.regex,
                  message: mobileConfig.message,
                },
              }}
              render={({ field }) => (
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder={mobileConfig.placeholder}
                  maxLength={mobileConfig.placeholder.length}
                  className="h-full min-w-0 flex-1 border-none bg-transparent px-3 text-sm font-medium text-[var(--dash-ink)] outline-none placeholder:text-[var(--dash-muted)]"
                  aria-invalid={Boolean(errors.mobileNumber)}
                  value={formatMobileDisplay(field.value || '', mobileConfig)}
                  onChange={(e) => {
                    const { value, rejected } = parseMobileInput(
                      e.target.value,
                      mobileConfig,
                    );
                    field.onChange(value);
                    if (rejected) {
                      setError('mobileNumber', {
                        type: 'pattern',
                        message: mobileConfig.message,
                      });
                    } else {
                      clearErrors('mobileNumber');
                    }
                  }}
                  onBlur={() => {
                    field.onBlur();
                    const value = field.value || '';
                    if (value && !mobileConfig.regex.test(value)) {
                      setError('mobileNumber', {
                        type: 'pattern',
                        message: mobileConfig.message,
                      });
                    }
                  }}
                />
              )}
            />
          </div>
          <FieldError message={errors.mobileNumber?.message} />
        </div>
        <div>
          <FieldLabel>Email</FieldLabel>
          <input
            type="text"
            inputMode="email"
            autoComplete="email"
            placeholder="name@company.com (optional)"
            readOnly={emailReadOnly}
            className={`${inputClass} ${emailReadOnly ? 'cursor-not-allowed opacity-80' : ''}`}
            aria-invalid={Boolean(errors.email)}
            {...register('email', {
              validate: (value) => {
                if (emailReadOnly) return true;
                return (
                  isValidCreateClientEmail(String(value || '')) ||
                  'Enter a valid email'
                );
              },
              setValueAs: (value) =>
                emailReadOnly
                  ? String(value ?? '')
                  : inputValidation(String(value ?? ''), {
                      emoji: true,
                      specialCharacters: false,
                    }),
              onChange: (event) => {
                if (emailReadOnly) return;
                event.target.value = inputValidation(event.target.value, {
                  emoji: true,
                  specialCharacters: false,
                });
                const value = String(event.target.value || '');
                if (!isValidCreateClientEmail(value)) {
                  setError('email', {
                    type: 'validate',
                    message: 'Enter a valid email',
                  });
                  return;
                }
                clearErrors('email');
              },
            })}
          />
          <FieldError message={errors.email?.message} />
          <p className="mt-1 mb-0 text-[11px] text-[var(--dash-muted)]">
            Login details are sent to mobile. If email is added, they are sent to both.
          </p>
        </div>
      </div>
    </SectionCard>
  );
}
